import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, { useState, useEffect, useRef } from "react";
import tw, { styled, css } from "twin.macro";
import "styled-components/macro";
import _ from "lodash";
import { Link } from "gatsby";
import DynamicContainer from "./DynamicContainer";
import { getContrastText } from "../utils";
import Icon from "./Icon";
import NextUpcomingGame from "./NextUpcomingGame";

const HeroContainer = styled.div`
  ${tw`w-full z-10`}

  height: ${({ height }) => (height ? `${height}px` : "725px")};

  > div {
    ${tw`relative`}
    height: ${({ height }) => (height ? `${height - 75}px` : "650px")};
  }
`;
const ProgressBar = styled.div`
  ${tw`absolute -top-1.5 sm:-top-1.5 left-0 h-1.5 sm:h-1.5 animate-[elongate_8s_linear_1]`}
  background-color: ${(props) => props.color}
`;
const AngledContainer = styled.div`
  ${tw`absolute -bottom-8 sm:-bottom-10 left-0 w-full bg-black -skew-y-2 z-10 flex items-center`}
  ${(props) => (props.site === "dawgs" ? tw`h-12 sm:h-20` : tw`h-16 sm:h-24`)}
`;

const LiveButtonContainer = styled(DynamicContainer)`
  ${tw`flex w-full`}
`;
const MediaContainer = styled.div`
  ${tw`absolute w-full h-full 
  opacity-0 transition-opacity duration-1000 ease-out`}
  ${({ $show }) => $show && tw`opacity-100 z-[5]`}
  ${({ dots = {}, overlay = {} }) =>
    (dots.enabled || overlay.enabled) &&
    css`
      &::after {
        content: "";
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        ${dots.enabled &&
        `
          background-image: radial-gradient(${dots.color}b3 30%, ${
          dots.color
        }4d 30%);
          background-position: ${dots.size / 2}px ${dots.size / 2}px;
          background-size: ${dots.size}px ${dots.size}px;
          background-color: ${overlay.enabled ? overlay.color : "transparent"};
        `}
        ${overlay.enabled &&
        `
          background-color: ${overlay.color};
          opacity: ${overlay.opacity}
        `}
      }
    `}
`;

const Image = styled(GatsbyImage)`
  ${tw`
    absolute w-full h-full 
  `}
`;
const Video = styled.video`
  ${tw`absolute w-full h-full object-cover pointer-events-none`}
`;

const CutoutContainer = styled.div`
  ${tw`hidden lg:block
    absolute w-[50%] h-full top-0
    opacity-0 transition-opacity duration-1000 ease-out
  `}
  ${({ $show }) => $show && tw`opacity-100 z-[5]`}

  ${({ position }) =>
    position === "Left"
      ? tw`right-[20%]`
      : position === "Center"
      ? tw`right-[10%]`
      : tw`right-0`}
  ${({ offset }) => css`
    transform: translateX(${offset}px);
  `}
`;

const CutoutImage = styled(GatsbyImage)`
  ${tw``}
`;

const TextContainer = styled(DynamicContainer)`
  ${tw`
    flex flex-col px-0 
  `}
  ${({ $show }) => $show && tw`z-[5]`}
`;

const BlurbContainer = styled.div`
  ${({ maxChar }) =>
    css`
      max-width: ${maxChar}ch;
    `}
`;

function transitionDelay() {
  let styles = "z-index: 6;";

  for (let i = 1; i < 7; i += 1) {
    styles += `
         .textItem:nth-child(${i}) {
           transition-delay: ${i / 5}s;
         }
       `;
  }

  return css`
    ${styles}
  `;
}

const TextContent = styled.div`
  ${tw`
    absolute top-1/2 -translate-y-1/2
    text-white even:text-right
    flex flex-col even:self-end
    px-4 sm:px-8 lg:pl-16 lg:pr-0
    lg:max-w-[50%] max-w-full overflow-hidden
  `}

  > * {
    ${tw`
        transition-[transform,opacity] duration-500
        -translate-x-4 opacity-0
    `}
  }

  ${({ $show }) => $show && transitionDelay()}

  ${({ $show }) =>
    $show &&
    css`
      > * {
        ${tw`opacity-100 translate-x-0 z-[6]`}
      }
    `}

  > :not(button) {
    text-shadow: 0 0 8px black;
  }

  > h2 {
    ${tw`font-bold mb-2`}

    &:nth-child(2) {
      ${tw`font-semibold`}
    }
  }

  > div {
    ${tw`text-xl sm:text-2xl font-semibold mb-4`}
  }

  button {
    ${tw`
        flex items-center
        mb-4 py-2 px-3
        text-base sm:text-lg font-semibold uppercase
        rounded-xl 
    `}

    > span {
      ${tw`mt-0.5`}
    }

    > div {
      ${tw`mr-1 h-6 brightness-0 invert`}
    }
  }

  &:nth-child(even) button {
    ${tw`float-right`}
  }
`;
const Button = styled.button`
  background-color: ${({ buttonBgColor }) =>
    buttonBgColor ? buttonBgColor : "#000000"};
  color: ${({ buttonTextColor }) =>
    buttonTextColor ? buttonTextColor : "#FFFFFF"};
  width: ${({ width }) => (width ? `${width}px` : "240px")};

  ${tw`transition hover:invert`}
`;

const IconContainer = styled.div`
  ${tw`w-8`}
`;

const PagingButtonContainer = styled.div`
  ${tw`text-white  absolute right-4 bottom-20 flex flex-col z-[6]`}
`;
const PagingButton = styled.div`
  ${tw`m-1 block p-2 transition-colors not-disabled:hover:bg-gray-200 cursor-pointer`}
  background-color: ${(props) => (props.active ? props.color : "gray")}
`;

const SlideshowHero = ({ data, height, site, color, selectedTeam }) => {
  const [slideCount, setSlideCount] = useState(0);
  const intervalRef = useRef();

  const slidesArr = _.get(data, "markdownRemark.frontmatter.hero.slides", []);

  const hideAndReveal = () => {
    setSlideCount((oldCount) => (oldCount + 1) % slidesArr.length);
  };

  const createInterval = () => {
    const interval = setInterval(hideAndReveal, 8000);
    intervalRef.current = interval;
  };

  const removeInterval = () => {
    clearInterval(intervalRef.current);
  };

  useEffect(() => {
    createInterval();

    return () => {
      removeInterval();
    };
  }, []);

  return (
    <HeroContainer height={height}>
      <div>
        {!_.isEmpty(slidesArr) &&
          slidesArr.map((slide, index) => {
            const {
              image,
              subheaders,
              headers,
              blurb,
              blurbLength,
              buttons,
              dots,
              overlay,
              cutout,
              video,
            } = slide;

            return (
              <React.Fragment key={index}>
                <MediaContainer
                  dots={dots}
                  overlay={overlay}
                  $show={index === slideCount}
                >
                  {!_.isNil(video?.publicURL) && (
                    <Video muted autoPlay loop playsInline>
                      <source src={video.publicURL} type="video/mp4" />
                    </Video>
                  )}
                  {_.isNil(video?.publicURL) && (
                    <Image
                      image={getImage(image)}
                      $show={index === slideCount}
                      alt="hero"
                    />
                  )}
                </MediaContainer>
                {!_.isNil(cutout?.image) && (
                  <CutoutContainer
                    $show={index === slideCount}
                    position={cutout.position}
                    offset={cutout.offset}
                  >
                    <CutoutImage
                      image={getImage(cutout.image)}
                      alt="cutout"
                      style={{ height: "100%" }}
                      objectFit="cover"
                    />
                  </CutoutContainer>
                )}
                <TextContainer $show={index === slideCount}>
                  <TextContent $show={index === slideCount}>
                    {(subheaders || []).map((subheader, hidx) => (
                      <div className="textItem" key={hidx}>
                        {subheader}
                      </div>
                    ))}
                    {(headers || []).map((header, hidx) => (
                      <h2 className="textItem" key={hidx}>
                        {header}
                      </h2>
                    ))}
                    <BlurbContainer
                      className="textItem"
                      maxChar={blurbLength || 50}
                    >
                      {blurb}
                    </BlurbContainer>
                    {(buttons || []).map(
                      ({ icon, label, url, color, width }, idx) => (
                        <Link to={url} className="textItem" key={idx}>
                          <Button
                            buttonBgColor={color}
                            buttonTextColor={getContrastText(color)}
                            width={width}
                          >
                            {icon && (
                              <IconContainer>
                                <Icon name={icon} />
                              </IconContainer>
                            )}
                            <span>{label}</span>
                          </Button>
                        </Link>
                      )
                    )}
                  </TextContent>
                </TextContainer>
              </React.Fragment>
            );
          })}
        {!_.isEmpty(slidesArr) && (
          <PagingButtonContainer>
            {slidesArr.map((slide, index) => (
              <PagingButton
                color={color}
                key={index}
                aria-label="Paging Button"
                disabled={slideCount === index}
                active={slideCount === index}
                onClick={() => {
                  setSlideCount(index);
                  removeInterval();
                  createInterval();
                }}
              />
            ))}
          </PagingButtonContainer>
        )}

        <AngledContainer>
          <ProgressBar key={slideCount} color={color} site={site} />
          <LiveButtonContainer>
            <NextUpcomingGame
              data={data}
              site={site}
              color={color}
              selectedTeam={selectedTeam}
            />
          </LiveButtonContainer>
        </AngledContainer>
      </div>
    </HeroContainer>
  );
};

export default SlideshowHero;
